import { c as createFocusTrap } from './focus-trap.esm-CoH9VLSd.js';

class HeaderNavigation extends HTMLElement {
  /** @type {NodeListOf<HTMLElement>} */ #controllers = this.querySelectorAll(
    '[aria-controls^="header-navigation--"]'
  );
  /** @type {HTMLElement|null} */ #activeItem;
  /** @type {AbortController|null} */ #scrollController = null;
  /** @type {import('focus-trap').FocusTrap} */ #focusTrap;
  /** @type {MegaMenuElement|null} */ #megaMenuElement;

  connectedCallback() {
    this.#controllers.forEach((controller) => {
      controller.addEventListener('mouseenter', () => this.#handleMouseenter(controller));
      controller.addEventListener('mouseleave', (event) => this.#handleMouseleave(event, controller));
      controller.addEventListener('keydown', (event) => this.#handleKeydown(event, controller));
    });

    this.#megaMenuElement = this.querySelector('mega-menu-element');
  }

  /**
   * Handle controller mouseenter
   * @param {HTMLElement} controller
   */
  #handleMouseenter(controller) {
    const target = controller.ariaControlsElements[0];
    if (!target) return;

    this.#open(controller, target);
  }

  /**
   * Handle controller mouseleave
   * @param {MouseEvent} event
   * @param {HTMLElement} controller
   */
  #handleMouseleave(event, controller) {
    const target = controller.ariaControlsElements[0];
    if (!target) return;

    const nextElement = event.relatedTarget;

    // Moving from the controller into the target should not close the panel.
    if (nextElement instanceof Element && (controller.contains(nextElement) || target.contains(nextElement))) {
      return this.#watchTargetMouseleave(controller, target);
    }

    // Wait one frame to let hover state settle before deciding to close.
    requestAnimationFrame(() => {
      if (controller.matches(':hover') || target.matches(':hover')) {
        this.#watchTargetMouseleave(controller, target);
        return;
      }

      this.#close(controller, target);
    });
  }

  /**
   * Close when the mouse leaves the expanded target.
   * @param {HTMLElement} controller
   * @param {HTMLElement} target
   */
  #watchTargetMouseleave(controller, target) {
    target.addEventListener('mouseleave', () => this.#close(controller, target), {
      once: true,
    });
  }

  /**
   * Handle controller keydown
   * @param {KeyboardEvent} event
   * @param {HTMLElement} controller
   */
  #handleKeydown(event, controller) {
    if (event.code !== 'Space') return;

    // Prevent default space key behavior
    event.preventDefault();

    const target = controller.ariaControlsElements[0];
    if (!target) return;

    // If the active item is open, close it, otherwise open it
    this.#activeItem ? this.#close(controller, target) : this.#open(controller, target);

    // Create the focus trap
    this.#focusTrap = createFocusTrap(target, {
      onDeactivate: () => this.#close(controller, target),
    });

    // Activate the focus trap once the transition ends
    target.addEventListener('transitionend', () => this.#focusTrap.activate(), { once: true });
  }

  /**
   * Open the active item
   * @param {HTMLElement} controller
   * @param {HTMLElement} target
   */
  #open(controller, target) {
    this.#activeItem = { controller, target };

    controller.ariaExpanded = 'true';
    target.ariaHidden = 'false';

    // Add scroll listener
    this.#addScrollListener(controller, target);
  }

  /**
   * Close the active item
   * @param {HTMLElement} controller
   * @param {HTMLElement} target
   */
  #close(controller, target) {
    this.#activeItem = null;

    controller.ariaExpanded = 'false';
    target.ariaHidden = 'true';

    //  Collapse all panels. Ref: mega-menu.js:43
    this.#megaMenuElement?.collapseAll?.();

    // Clean up scroll listener
    this.#scrollController?.abort();
    this.#scrollController = null;

    // Clean up focus trap
    if (this.#focusTrap) this.#focusTrap = null;
  }

  /**
   * Add scroll listener to the active item
   * @param {HTMLElement} controller
   * @param {HTMLElement} target
   */
  #addScrollListener(controller, target) {
    // Clean up any existing scroll listener
    this.#scrollController?.abort();

    this.#scrollController = new AbortController();
    window.addEventListener('scroll', () => this.#close(controller, target), {
      once: true,
      signal: this.#scrollController.signal,
    });
  }
}

customElements.define('header-navigation', HeaderNavigation);
